import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Target, Pencil, Star, Palette, Search, Triangle, Compass, Gem, Globe, Award as StarBadge, Box, Archive, Library, BookOpen, Film, Tv, Gamepad2, Footprints, Users, MessageSquare, StarHalf, Heart, TrendingUp, Megaphone, Trophy, Flame, Calendar, MapPin, CheckCircle2, Award, Shield, Crown, PenTool, Lock, ChevronDown, ChevronUp, Loader2, Eye, ScrollText, HelpCircle, Camera } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { SocialShareModal } from "../components/SocialShareModal";
import { useTranslation } from "../i18n";

const API = '/api';

const ICON_MAP = {
  target: Target, pencil: Pencil, star: Star, palette: Palette, search: Search,
  triangle: Triangle, compass: Compass, gem: Gem, globe: Globe, star_badge: StarBadge,
  box: Box, boxes: Box, archive: Archive, library: Library, book: BookOpen,
  film: Film, tv: Tv, gamepad: Gamepad2, footprints: Footprints, users: Users,
  message: MessageSquare, star_half: StarHalf, heart: Heart, trending_up: TrendingUp,
  megaphone: Megaphone, trophy: Trophy, flame: Flame, calendar: Calendar,
  map_pin: MapPin, check_circle: CheckCircle2, award: Award, shield: Shield,
  crown: Crown, pen_tool: PenTool, lock: Lock, eye: Eye, scroll: ScrollText,
  princess_donut: HelpCircle,
};

const ICON_BG_COLORS = {
  target: "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400",
  pencil: "bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
  star: "bg-yellow-100 text-yellow-600 dark:bg-yellow-950 dark:text-yellow-400",
  palette: "bg-pink-100 text-pink-600 dark:bg-pink-950 dark:text-pink-400",
  search: "bg-cyan-100 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400",
  triangle: "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400",
  compass: "bg-teal-100 text-teal-600 dark:bg-teal-950 dark:text-teal-400",
  gem: "bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-400",
  globe: "bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400",
  star_badge: "bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-400",
  box: "bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400",
  boxes: "bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400",
  archive: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  library: "bg-violet-100 text-violet-600 dark:bg-violet-950 dark:text-violet-400",
  book: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  film: "bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-400",
  tv: "bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-400",
  gamepad: "bg-lime-100 text-lime-700 dark:bg-lime-950 dark:text-lime-400",
  footprints: "bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-400",
  users: "bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
  message: "bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400",
  star_half: "bg-yellow-100 text-yellow-600 dark:bg-yellow-950 dark:text-yellow-400",
  heart: "bg-red-100 text-red-500 dark:bg-red-950 dark:text-red-400",
  trending_up: "bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400",
  megaphone: "bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-400",
  trophy: "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400",
  flame: "bg-orange-100 text-orange-500 dark:bg-orange-950 dark:text-orange-400",
  calendar: "bg-teal-100 text-teal-600 dark:bg-teal-950 dark:text-teal-400",
  map_pin: "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400",
  check_circle: "bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400",
  award: "bg-yellow-100 text-yellow-600 dark:bg-yellow-950 dark:text-yellow-400",
  shield: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400",
  crown: "bg-amber-100 text-amber-500 dark:bg-amber-950 dark:text-amber-400",
  pen_tool: "bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400",
  lock: "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500",
  eye: "bg-violet-100 text-violet-600 dark:bg-violet-950 dark:text-violet-400",
  scroll: "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400",
  princess_donut: "bg-pink-100 text-pink-500 dark:bg-pink-950 dark:text-pink-400",
};

function AchievementCard({ achievement, onShare }) {
  const { t, lang } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isEarned = achievement.earned;
  const isSecret = achievement.secret;
  const hasImage = achievement.icon_image;
  const IconComponent = ICON_MAP[achievement.icon] || HelpCircle;
  const colorClass = ICON_BG_COLORS[achievement.icon] || "bg-muted text-muted-foreground";

  return (
    <div
      className={`rounded-xl border bg-card p-3 transition-all cursor-pointer ${
        isEarned ? "border-border" : isSecret ? "border-dashed border-muted-foreground/30 opacity-60" : "border-border/50 opacity-75"
      }`}
      onClick={() => setExpanded(!expanded)}
      data-testid={`achievement-${achievement.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${
          isEarned && hasImage ? "" : isEarned ? colorClass : "bg-muted text-muted-foreground"
        }`}>
          {isEarned && hasImage ? (
            <img src={achievement.icon_image} alt="" className="w-11 h-11 object-cover rounded-lg" />
          ) : isSecret && !isEarned ? (
            <HelpCircle className="w-5 h-5" strokeWidth={1.8} />
          ) : (
            <IconComponent className="w-5 h-5" strokeWidth={1.8} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`text-sm font-semibold truncate ${isSecret && !isEarned ? "italic text-muted-foreground" : ""}`}>
              {achievement.name}
            </span>
            {isEarned ? (
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-dashed border-muted-foreground/40 flex-shrink-0" />
            )}
          </div>
          <p className={`text-xs line-clamp-2 mt-0.5 ${isSecret && !isEarned ? "italic text-muted-foreground/60" : "text-muted-foreground"}`}>
            {achievement.description}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isEarned && onShare && (
            <button
              onClick={(e) => { e.stopPropagation(); onShare(achievement); }}
              className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
              data-testid={`share-achievement-${achievement.id}`}
              title={t.achievementsExtra.shareOnInstagram}
            >
              <Camera className="w-3.5 h-3.5" />
            </button>
          )}
          <div className="text-muted-foreground">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {expanded && isEarned && achievement.earned_at && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <p className="text-xs text-primary mt-2 pl-14">
              {t.achievements.earnedOn(new Date(achievement.earned_at).toLocaleDateString(lang === "en" ? "en-US" : "es-ES", { day: "numeric", month: "long", year: "numeric" }))}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CategorySection({ category, isLast, onShareAchievement }) {
  const { t } = useTranslation();
  const status = category.completed ? "completed" : category.unlocked ? "active" : "locked";
  const isSecret = category.secret;
  
  const dotColor = isSecret
    ? "bg-purple-500 dark:bg-purple-400"
    : status === "completed" ? "bg-primary" : status === "active" ? "bg-amber-400" : "bg-gray-300 dark:bg-gray-600";
  const lineColor = status === "completed" ? "bg-primary" : status === "active" ? "bg-amber-400" : "";
  const remaining = category.total_count - category.earned_count;

  return (
    <div className="relative" data-testid={`category-${category.id}`}>
      <div className="flex items-start gap-4">
        {/* Dot + Line */}
        <div className="flex flex-col items-center flex-shrink-0 w-7">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${dotColor}`}>
            {status === "completed" ? (
              <CheckCircle2 className="w-4 h-4 text-white" />
            ) : isSecret ? (
              <HelpCircle className="w-4 h-4 text-white" />
            ) : status === "active" ? (
              <span className="w-2.5 h-2.5 rounded-full bg-white" />
            ) : (
              <Lock className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
            )}
          </div>
          {!isLast && (
            <div
              className={`w-0.5 flex-1 min-h-[24px] mt-1 ${status === "locked" ? "" : lineColor}`}
              style={status === "locked" ? { borderLeft: "2px dashed rgb(209 213 219)" } : {}}
            />
          )}
        </div>

        {/* Category content */}
        <div className="flex-1 min-w-0 pb-6">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-bold">{t.achievementStages?.[category.name] || category.name}</h2>
            {status === "locked" && <Lock className="w-4 h-4 text-muted-foreground" />}
          </div>

          {status !== "locked" ? (
            <>
              {status === "active" && remaining > 0 && !isSecret && (
                <p className="text-xs text-muted-foreground mb-3">
                  {t.achievementsExtra.remainingAchievements(remaining)}
                </p>
              )}
              {isSecret && (
                <p className="text-xs text-purple-500 dark:text-purple-400 mb-3">
                  {t.achievementsExtra.hiddenAchievements}
                </p>
              )}
              {status === "completed" && !isSecret && (
                <p className="text-xs text-primary mb-3">{t.achievementsExtra.stageCompleted}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {category.achievements.map((ach) => (
                  <AchievementCard key={ach.id} achievement={ach} onShare={onShareAchievement} />
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function Achievements({ user }) {
  const { t, lang } = useTranslation();
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareAch, setShareAch] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/achievements?lang=${lang}`),
      axios.get(`${API}/users/me/streak`).catch(() => ({ data: { current_streak: 0, total_days: 0 } })),
    ]).then(([achRes, streakRes]) => {
      setData(achRes.data);
      setStreak(streakRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [lang]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  const categories = data?.categories || [];
  const totalEarned = data?.earned_count || 0;
  const totalAch = data?.total || 0;
  const progressPercent = totalAch > 0 ? (totalEarned / totalAch) * 100 : 0;

  // Separate secret and regular categories
  const regularCategories = categories.filter(c => !c.secret);
  const secretCategories = categories.filter(c => c.secret);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="achievements-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t.achievements.title}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t.achievements.subtitle}
        </p>
      </div>

      {/* Overall progress + streak */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Trophy className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-semibold">{t.achievementsExtra.overallProgress}</span>
            </div>
            <Progress value={progressPercent} className="h-2 mb-2" />
            <p className="text-xs text-muted-foreground">{totalEarned} / {totalAch} {t.achievementsExtra.achievementsUnlocked}</p>
          </CardContent>
        </Card>
        {streak && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="text-sm font-semibold">{t.achievementsExtra.yourStreak}</span>
              </div>
              <div className="flex items-baseline gap-4">
                <div>
                  <span className="text-2xl font-bold">{streak.current_streak}</span>
                  <span className="text-xs text-muted-foreground ml-1">{t.achievementsExtra.consecutiveDays}</span>
                </div>
                <div>
                  <span className="text-lg font-semibold text-muted-foreground">{streak.total_days}</span>
                  <span className="text-xs text-muted-foreground ml-1">{t.achievementsExtra.totalDays}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Regular timeline */}
      <div data-testid="achievements-timeline">
        {regularCategories.map((cat, index) => (
          <motion.div
            key={cat.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.06, duration: 0.25 }}
          >
            <CategorySection
              category={cat}
              isLast={index === regularCategories.length - 1 && secretCategories.length === 0}
              onShareAchievement={setShareAch}
            />
          </motion.div>
        ))}
      </div>

      {/* Secret achievements */}
      {secretCategories.map((cat, index) => (
        <motion.div
          key={cat.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: (regularCategories.length + index) * 0.06, duration: 0.25 }}
        >
          <CategorySection
            category={{ ...cat, unlocked: true }}
            isLast={index === secretCategories.length - 1}
            onShareAchievement={setShareAch}
          />
        </motion.div>
      ))}

      <SocialShareModal
        isOpen={!!shareAch}
        onClose={() => setShareAch(null)}
        type="achievement"
        username={user?.username || user?.name || "usuario"}
        title=""
        coverUrl=""
        achievementName={shareAch?.name || ""}
        achievementIcon={shareAch?.icon || ""}
        achievementDescription={shareAch?.description || ""}
        achievementImage={shareAch?.icon_image || ""}
        totalEarned={data?.categories?.reduce((sum, c) => sum + (c.achievements?.filter(a => a.earned).length || 0), 0) || 0}
        totalAchievements={data?.categories?.reduce((sum, c) => sum + (c.achievements?.length || 0), 0) || 0}
      />
    </div>
  );
}

export default Achievements;
